import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
const MAX_HISTORY = 100;
const BATCH_DEBOUNCE_MS = 300;
export type UndoRedoSnapshot = {
  value: string;
  selectionStart: number;
};

/**
 * Pure undo/redo history manager — no React dependency.
 * Maintains past/future stacks of { value, selectionStart } snapshots.
 *
 * Batching: callers pass `batch: true` for normal keystrokes so entries
 * within 300ms collapse into one. Programmatic changes (formatting,
 * suggestion insertion) omit the flag to always create a new entry.
 */
export class UndoRedoHistory {
  past: UndoRedoSnapshot[] = [];
  future: UndoRedoSnapshot[] = [];
  private lastBatchTime = 0;
  private lastWasBatch = false;
  private resetBatchState(): void {
    this.lastWasBatch = false;
    this.lastBatchTime = 0;
  }
  pushState(snapshot: UndoRedoSnapshot, options?: {
    batch?: boolean;
  }): void {
    const current = this.past[this.past.length - 1];

    // Ignore syncs that don't change the value. Cursor-only changes are not tracked.
    if (current?.value === snapshot.value) {
      return;
    }
    const now = Date.now();
    const shouldBatch = options?.batch ?? false;

    // Only collapse if both the current and previous push were batched
    if (shouldBatch && this.lastWasBatch && this.past.length > 0) {
      const elapsed = now - this.lastBatchTime;
      if (elapsed < BATCH_DEBOUNCE_MS) {
        // Collapse into the most recent entry
        this.past[this.past.length - 1] = snapshot;
        this.lastBatchTime = now;
        return;
      }
    }
    this.past.push(snapshot);

    // Cap history size
    if (this.past.length > MAX_HISTORY) {
      this.past.splice(0, this.past.length - MAX_HISTORY);
    }

    // Any new edit clears the redo stack
    this.future = [];
    this.lastWasBatch = shouldBatch;
    this.lastBatchTime = shouldBatch ? now : 0;
  }
  reset(snapshot: UndoRedoSnapshot): void {
    this.past = [snapshot];
    this.future = [];
    this.resetBatchState();
  }
  undo(): UndoRedoSnapshot | null {
    if (this.past.length <= 1) {
      return null;
    }
    const current = this.past.pop()!;
    this.future.push(current);
    this.resetBatchState();
    return this.past[this.past.length - 1] ?? null;
  }
  redo(): UndoRedoSnapshot | null {
    if (this.future.length === 0) {
      return null;
    }
    const next = this.future.pop()!;
    this.past.push(next);
    this.resetBatchState();
    return next;
  }
  get canUndo(): boolean {
    return this.past.length > 1;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }
}

/**
 * React hook wrapper — keeps a single UndoRedoHistory instance alive
 * for the lifetime of the component.
 */
export function useUndoRedo() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new UndoRedoHistory();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}